import React, { useState, useEffect, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { useBlackFriday } from '../../contexts/BlackFridayContext';

/** Initial banner reveal — subtle lift + fade (aligned with hero / glass UI) */
const sparkle = keyframes`
  0% { transform: scale(0) rotate(0deg); opacity: 0; }
  50% { transform: scale(1) rotate(180deg); opacity: 1; }
  100% { transform: scale(0) rotate(360deg); opacity: 0; }
`;

const smokeRise = keyframes`
  0% { transform: translate(-20%, 30%) scale(0.7); opacity: 0.2; }
  50% { transform: translate(10%, 0%) scale(1); opacity: 0.45; }
  100% { transform: translate(25%, -30%) scale(1.3); opacity: 0; }
`;

const messageCrossfade = keyframes`
  from {
    opacity: 0;
    transform: translateY(5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const BannerContainer = styled.div`
  position: relative;
  z-index: 0;
  width: 100%;
  flex-shrink: 0;
  background: linear-gradient(
    180deg,
    rgba(157, 109, 73, 0.97) 0%,
    ${props => props.theme.colors.accent} 100%
  );
  color: #fff;
  padding: 0.42rem clamp(1rem, 4vw, 2.5rem);
  text-align: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.14);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.14),
    0 4px 20px rgba(16, 29, 64, 0.12);
  opacity: ${props => (props['data-visible'] === 'true' ? 1 : 0)};
  transform: translateY(${props => (props['data-visible'] === 'true' ? '0' : '-8px')});
  pointer-events: ${props => (props['data-visible'] === 'true' ? 'auto' : 'none')};
  transition:
    opacity 0.55s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.55s cubic-bezier(0.22, 1, 0.36, 1);

  @media (prefers-reduced-motion: reduce) {
    transform: none;
    transition: opacity 0.2s ease;
  }

  @media (max-width: 768px) {
    padding: 0.36rem 0.75rem;
  }
`;

const UrgentBannerContainer = styled(BannerContainer)`
  background: linear-gradient(180deg, ${props => props.theme.colors.accent} 0%, #8b5e41 100%);
`;

const HalloweenBannerContainer = styled(BannerContainer)`
  background: linear-gradient(180deg, #1c1630 0%, #14101f 100%);
  border-bottom: 1px solid rgba(201, 162, 39, 0.32);
  box-shadow:
    inset 0 1px 0 rgba(201, 162, 39, 0.18),
    0 4px 20px rgba(20, 16, 31, 0.28);
`;

const MagicalBannerContainer = styled(BannerContainer)`
  background: linear-gradient(95deg, #0e1a38 0%, #152a52 48%, #0e1a38 100%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  &::before,
  &::after {
    content: '';
    position: absolute;
    background: white;
    border-radius: 50%;
    pointer-events: none;
    animation: ${sparkle} 3.5s ease-in-out infinite;
  }

  &::before {
    width: 4px;
    height: 4px;
    top: 20%;
    left: 20%;
    animation-delay: 0.5s;
  }

  &::after {
    width: 3px;
    height: 3px;
    top: 60%;
    right: 20%;
    animation-delay: 1s;
  }

  & span::before,
  & span::after {
    content: '';
    position: absolute;
    background: white;
    border-radius: 50%;
    pointer-events: none;
    animation: ${sparkle} 3.5s ease-in-out infinite;
  }

  & span::before {
    width: 5px;
    height: 5px;
    top: 40%;
    left: 30%;
    animation-delay: 0.2s;
  }

  & span::after {
    width: 3px;
    height: 3px;
    top: 70%;
    right: 30%;
    animation-delay: 1.5s;
  }
`;

const BlackFridayBannerContainer = styled(BannerContainer)`
  background: radial-gradient(circle at 20% 20%, rgba(247, 198, 84, 0.22), transparent 55%),
    linear-gradient(125deg, #060608 0%, #12121a 50%, #1a1520 100%);
  border-bottom: 1px solid rgba(247, 198, 84, 0.28);
  box-shadow:
    inset 0 1px 0 rgba(247, 198, 84, 0.22),
    0 8px 36px rgba(0, 0, 0, 0.35);

  &::before,
  &::after {
    content: '';
    position: absolute;
    animation: ${smokeRise} 18s infinite;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, rgba(5, 5, 8, 0) 60%);
    filter: blur(18px);
    opacity: 0.35;
    pointer-events: none;
  }

  &::before {
    width: 180px;
    height: 180px;
    top: 10%;
    left: 5%;
  }

  &::after {
    width: 220px;
    height: 220px;
    bottom: -10%;
    right: 10%;
    animation-delay: 6s;
  }
`;

const GreenBannerContainer = styled(BannerContainer)`
  background: linear-gradient(180deg, #2e7d32 0%, #256628 100%);
`;

const BannerContent = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.65rem 1rem;
  position: relative;
  z-index: 1;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: clamp(0.78rem, 1.5vw, 0.875rem);
  font-weight: 500;
  line-height: 1.45;
  letter-spacing: 0.02em;
  animation: ${messageCrossfade} 0.42s cubic-bezier(0.22, 1, 0.36, 1) both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  &.black-friday-banner {
    flex-direction: column;
    gap: 0.35rem;
    text-align: center;
  }

  @media (max-width: 768px) {
    gap: 0.5rem 0.75rem;
    font-size: 0.74rem;
  }
`;

const BannerMessage = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.12rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;

  strong {
    font-size: clamp(0.82rem, 1.6vw, 0.95rem);
    font-weight: 700;
    color: #fff3d6;
    letter-spacing: 0.05em;
  }

  span {
    font-size: 0.8rem;
    letter-spacing: 0.03em;
    opacity: 0.88;
    font-weight: 500;
    text-transform: none;
  }
`;

const BannerLink = styled(Link)`
  flex-shrink: 0;
  color: #fff;
  text-decoration: none;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-weight: 600;
  font-size: 0.65rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 0.45rem 0.95rem;
  border: 1px solid rgba(255, 255, 255, 0.85);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(8px);
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: #fff;
    color: ${props => props.theme.colors.primary};
    border-color: #fff;
  }

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 2px;
  }
`;

const BannerExternalLink = styled.a`
  flex-shrink: 0;
  color: #fff;
  text-decoration: none;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-weight: 600;
  font-size: 0.65rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 0.45rem 0.95rem;
  border: 1px solid rgba(255, 255, 255, 0.85);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(8px);
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: #fff;
    color: ${props => props.theme.colors.primary};
    border-color: #fff;
  }

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 2px;
  }
`;

const AnnouncementBanner = () => {
  const [visible, setVisible] = useState(true);
  const [messageIndex, setMessageIndex] = useState(0);
  const location = useLocation();
  const { isActive: isBlackFriday, message: blackFridayMessage } = useBlackFriday();
  const isDayTicketsPage = location.pathname.includes('/day-tickets');
  const isFarmShopPage = location.pathname.startsWith('/farm-shop') || location.pathname.includes('/retail-village/shops/farm-shop');
  const isSummerSafariPage = location.pathname.includes('/belvoir-summer-safari') || location.pathname === '/summer-safari';
  const isHalloweenPage = location.pathname.includes('/halloween-belvoir-castle') || location.pathname.includes('/belvoir-wicked-night');
  const isVineyardTourPage = location.pathname.includes('/vineyard-tour');
  const isChristmasPage = location.pathname.startsWith('/christmas');
  const endOfDay = (dateStr) => {
    const d = new Date(dateStr);
    d.setHours(23, 59, 59, 999);
    return d;
  };

  const isMessageActive = (message) => {
    const now = new Date();
    if (message.startDate && now < new Date(message.startDate)) return false;
    if (message.endDate && now > endOfDay(message.endDate)) return false;
    return true;
  };

  const activeMessages = (messages) => messages.filter(isMessageActive);

  const christmasDeckTheHallsMessage = {
    text: "Christmas at Belvoir — Deck the Halls",
    type: "magical",
    duration: 5000,
    link: "/christmas",
    buttonLabel: "Find Out More",
    endDate: "2026-12-30"
  };

  const summerSafariMessage = {
    text: "Belvoir Summer Safari — Follow the clues, find the animals",
    type: "urgent",
    duration: 5000,
    link: "/belvoir-summer-safari",
    buttonLabel: "Book Now",
    endDate: "2026-09-01"
  };

  const freeIceCreamMessage = {
    text: "Free ice cream for every child this summer holiday",
    type: "urgent",
    duration: 5000,
    link: "/belvoir-summer-safari",
    buttonLabel: "Book Now",
    endDate: "2026-09-01"
  };

  const enchantedNightMessage = {
    text: "Belvoir's Enchanted Night — October school holidays. A pesky witch has cursed Belvoir.",
    type: "halloween",
    duration: 5000,
    link: "/halloween-belvoir-castle",
    buttonLabel: "Discover",
    endDate: "2026-10-31"
  };

  const vineyardTourMessage = {
    text: "Vineyard Tours — Saturdays 15 & 29 August",
    type: "urgent",
    duration: 5000,
    link: "/whats-on/vineyard-tour",
    buttonLabel: "Book Now",
    endDate: "2026-08-29"
  };

  const farmShopMessage = {
    text: "Click & Collect Only - Deliveries Coming Soon",
    type: "urgent",
    duration: 5000,
    link: null,
    buttonLabel: null,
    noLink: true,
    endDate: "2026-12-31"
  };

  const flowerAndGardenShowMessage = {
    text: "Belvoir Flower & Garden Show — Limited Tickets Remain",
    type: "urgent",
    duration: 5000,
    link: "/event/cG9zdDoxMjc=",
    buttonLabel: "Book Now",
    startDate: "2026-06-22",
    endDate: "2026-07-12"
  };

  const blackFridayTakeover = isBlackFriday ? {
    text: blackFridayMessage?.headline || "Black Friday: 20% Off Entry and Santa Tickets + Additional Family Saving",
    subtext: blackFridayMessage?.body || "Bring your visiting party to Belvoir Castle and watch 20% fall away automatically.",
    type: "blackFriday",
    duration: 8000,
    link: blackFridayMessage?.ctaLink || "/christmas/your-festive-visit",
    buttonLabel: blackFridayMessage?.ctaLabel || "Unlock 20% Off"
  } : null;

  const getCurrentMessages = () => {
    if (blackFridayTakeover) {
      return [blackFridayTakeover];
    }
    if (isFarmShopPage) {
      return activeMessages([farmShopMessage]);
    }
    if (isHalloweenPage) {
      return activeMessages([{
        ...enchantedNightMessage,
        isExternal: true,
        link: "https://booking.belvoircastle.com",
        buttonLabel: "Book Tickets"
      }]);
    }
    const rotationMessages = [flowerAndGardenShowMessage];
    if (!isSummerSafariPage) {
      rotationMessages.push(summerSafariMessage, freeIceCreamMessage);
    }
    if (!isVineyardTourPage) {
      rotationMessages.push(vineyardTourMessage);
    }
    if (!isChristmasPage) {
      rotationMessages.push(christmasDeckTheHallsMessage);
    }
    return activeMessages(rotationMessages);
  };

  const currentMessages = useMemo(() => getCurrentMessages(), [isDayTicketsPage, isFarmShopPage, isSummerSafariPage, isHalloweenPage, isVineyardTourPage, isChristmasPage, location.pathname, blackFridayTakeover]);

  useEffect(() => {
    if (currentMessages.length === 0) {
      return;
    }

    // Reset to first message when component mounts or messages change
    setMessageIndex(0);

    const rotateMessages = () => {
      if (currentMessages.length <= 1) return;
      setMessageIndex(prevIndex => (prevIndex + 1) % currentMessages.length);
    };

    setVisible(true);

    let rotationTimer;
    if (currentMessages.length > 1) {
      rotationTimer = setInterval(rotateMessages, 10000);
    }

    return () => {
      if (rotationTimer) clearInterval(rotationTimer);
    };
  }, [currentMessages]);

  const getBannerContainer = () => {
    if (currentMessages.length === 0) {
      return BannerContainer;
    }

    const currentMessage = currentMessages[messageIndex] || currentMessages[0];

    if (currentMessage.type === 'blackFriday') {
      return BlackFridayBannerContainer;
    }

    if (isDayTicketsPage) {
      return UrgentBannerContainer;
    }

    if (currentMessage.type === 'halloween' || isHalloweenPage || (currentMessage.text && (currentMessage.text.includes('Halloween') || currentMessage.text.includes('Fangula') || currentMessage.text.includes('Enchanted Night')))) {
      return HalloweenBannerContainer;
    }

    return currentMessage.type === "magical" ? MagicalBannerContainer : currentMessage.type === "green" ? GreenBannerContainer : UrgentBannerContainer;
  };

  const BannerComponent = getBannerContainer();

  // Don't render anything if no messages to show
  if (currentMessages.length === 0) {
    return null;
  }

  const currentMessage = currentMessages[messageIndex] || currentMessages[0];

  return (
    <BannerComponent data-visible={visible.toString()}>
      <BannerContent
        key={`msg-${messageIndex}-${currentMessage.text?.slice(0, 24) ?? 'bf'}`}
        className={currentMessage.type === 'blackFriday' ? 'black-friday-banner' : undefined}
      >
        {currentMessage.type === 'blackFriday' ? (
          <BannerMessage>
            <strong>{currentMessage.text}</strong>
            {currentMessage.subtext && <span>{currentMessage.subtext}</span>}
          </BannerMessage>
        ) : (
          currentMessage.text
        )}
        {currentMessage.noLink ? null : (
          currentMessage.isExternal ? (
            <BannerExternalLink href={currentMessage.link} target="_blank" rel="noopener noreferrer">
              {currentMessage.buttonLabel || "Read More"}
            </BannerExternalLink>
          ) : currentMessage.link ? (
            <BannerLink to={currentMessage.link}>
              {currentMessage.buttonLabel || "Book Now"}
            </BannerLink>
          ) : null
        )}
        {currentMessage.type === "magical" && <span />}
      </BannerContent>
    </BannerComponent>
  );
};

export default AnnouncementBanner; 